import { defineStore } from "pinia";
import PermissionsResolver from "@rmp/core/api/authorization/permissionsResolver";
import InvalidOperationException from "@rmp/core/exceptions/invalidOperationException";
import { ProfileType } from "@rmp/core/types/ProfileType";
import User from "@rmp/core/stores/user/types/user";
import AbortService from "@rmp/core/services/abortService";
import { AccountController } from "@rmp/core/api/account";
import { plainToInstance } from "class-transformer";
import ApiLegalEntityProfile from "@rmp/core/api/types/account/profile/apiLegalEntityProfile";
import ApiEntrepreneurProfile from "@rmp/core/api/types/account/profile/apiEntrepreneurProfile";
import ApiForeignOrganizationProfile from "@rmp/core/api/types/account/profile/apiForeignOrganizationProfile";
import { ApiAccountProfileBase } from "@rmp/core/api/types/account/profile/apiAccountProfileBase";
import SecurityService from "@rmp/core/services/auth/securityService";
import { AccountProfileBase } from "@rmp/core/stores/user/types/accountProfileBase";
import { addDays, differenceInDays } from "date-fns";
import { LegalEntityController } from "@rmp/core/api/counterparty/legalEntity";
import { StaffController } from "@rmp/core/api/staff";
import { getTypePersonalAccount, TypePersonalAccount } from "@rmp/core/utils/getTypePersonalAccount";
import { safeFetch, strictFetch } from "@rmp/core/utils/safeFetch";
import { HEAD_DIVISION_ID } from "@rmp/core/constants/head";
import { isApiOfficeEmployeeProfile } from "@rmp/core/utils/isApiOfficeEmployeeProfile";
import { EntrepreneurController } from "@rmp/core/api/entrepreneur";
import {
	ApiCounterpartyEmployeeEntrepreneur,
	ApiCounterpartyEmployeePersistedLegalEntity,
	ApiOfficeEmployeePersisted
} from "@rmp/api-gateway";
import { AppsController } from "@rmp/core/api/apps";
import { useAppStore } from "@rmp/core/stores/appStore";

const securityService = new SecurityService();
const abortService = new AbortService();
const accountController = new AccountController(abortService);
const legalEntityController = new LegalEntityController(abortService);
const staffController = new StaffController(abortService);
const entrepreneurController = new EntrepreneurController(abortService);
const appsController = new AppsController(abortService);

const PASSWORD_EXPIRY_NOTIFICATION_START_TIME = addDays(new Date(), 10).getTime();

export interface UserState {
	permissions: string[],
	initialized: boolean,
	user: User
}

const getDefaultState = (): UserState => {
	return {
		permissions: [],
		initialized: false,
		user: new User()
	};
};

const permissionsResolver = new PermissionsResolver();

export const useUserStore = defineStore({
	id: "user",
	state: (): UserState => getDefaultState(),
	getters: {
		profile(state: UserState) {
			return state.user.profile;
		},
		isFromHeadOffice(state) {
			if(!isApiOfficeEmployeeProfile(state.user.profile)) return false;
			
			return state.user.profile.divisionId === HEAD_DIVISION_ID;
		},
		isMasterAccount(state: UserState) {
			if(state.user.profile) {
				switch (state.user.profile.type) {
					case ProfileType.MASTER_ACCOUNT_LEGAL_ENTITY:
					case ProfileType.MASTER_ACCOUNT_ENTREPRENEUR:
					case ProfileType.MASTER_ACCOUNT_FOREIGN_ORGANIZATION:
						return true;
					default:
						return false;
				}
			}
		},
		passwordExpired(state: UserState) {
			return Date.now() <= state.user.passwordExpiresAt;
		},
		passwordCloseExpiration(state: UserState) {
			return state.user.passwordExpiresAt <= PASSWORD_EXPIRY_NOTIFICATION_START_TIME;
		},
		passwordExpirationRemainingDays(state: UserState) {
			return Math.max(0, differenceInDays(state.user.passwordExpiresAt, new Date()));
		},
		isCounterpartyEmployee(state: UserState) {
			if(state.user.profile) {
				switch (state.user.profile.type) {
					case ProfileType.COUNTERPARTY_LEGAL_ENTITY_EMPLOYEE:
					case ProfileType.COUNTERPARTY_ENTREPRENEUR_EMPLOYEE:
					case ProfileType.COUNTERPARTY_PHYSICAL_PERSON_EMPLOYEE:
					case ProfileType.COUNTERPARTY_FOREIGN_ORGANIZATION_EMPLOYEE:
						return true;
					default:
						return false;
				}
			}
		},
		isEntrepreneurEmployee(state: UserState) {
			if(!state.user.profile) return;
			
			return state.user.profile.type === ProfileType.COUNTERPARTY_ENTREPRENEUR_EMPLOYEE;
		},
		isLegalEntityEmployee(state: UserState) {
			if(!state.user.profile) return;
			
			return state.user.profile.type === ProfileType.COUNTERPARTY_LEGAL_ENTITY_EMPLOYEE;
		},
		isOfficeEmployee(state: UserState) {
			if(!state.user.profile)
				return;
			
			return state.user.profile.type === ProfileType.OFFICE_EMPLOYEE;
		},
		userName(state: UserState) {
			return state.user.firstName + " " + state.user.middleName;
		}
	},
	actions: {
		async initialize() {
			if(this.initialized)
				throw new InvalidOperationException();
			
			abortService.initialize();
			
			try {
				await Promise.all([
					this.fetchUserinfo(),
					this.fetchPermissions(),
					this.fetchProfiles(),
					this.fetchApps()
				]);
				
				this.initialized = true;
			} catch (error) {
				useAppStore().handleActionError(error);
			}
		},
		destroy() {
			abortService.abort();
		},
		async fetchPermissions() {
			try {
				this.permissions = await permissionsResolver.resolve();
			} catch (error) {
				useAppStore().handleActionError(error);
			}
		},
		check(requiredPermissions: string[], any = true): boolean {
			if(!this.initialized)
				throw new InvalidOperationException();
			
			if(!requiredPermissions.length)
				return true;
			
			if(any) {
				return requiredPermissions.some(x => this.permissions.includes(x));
			} else {
				return requiredPermissions.every(x => this.permissions.includes(x));
			}
		},
		async fetchProfiles() {
			this.user.isProfilesLoading = true;
			
			try {
				let profiles = await accountController.getAccountProfiles();
				
				this.user.profiles = profiles.map((x) => {
					return plainToInstance(AccountProfileBase, {
						...x,
						...x.profile
					});
				});
			} catch (error) {
				useAppStore().handleActionError(error);
			} finally {
				this.user.isProfilesLoading = false;
			}
		},
		async fetchApps() {
			try {
				this.user.apps = await strictFetch(() => appsController.getApps());
			} catch (error) {
				useAppStore().handleActionError(error);
			}
		},
		async fetchUserinfo() {
			this.user.isUserinfoLoading = true;
			
			try {
				let { account, profile, passwordExpiresAt } = await securityService.getUser();
				
				this.user.account = account;
				this.user.passwordExpiresAt = passwordExpiresAt;
				
				if(profile) {
					switch (profile.type) {
						case ProfileType.MASTER_ACCOUNT_LEGAL_ENTITY:
							this.user.profile = plainToInstance(ApiLegalEntityProfile, profile);
							break;
						case ProfileType.MASTER_ACCOUNT_ENTREPRENEUR:
							this.user.profile = plainToInstance(ApiEntrepreneurProfile, profile);
							break;
						case ProfileType.MASTER_ACCOUNT_FOREIGN_ORGANIZATION:
							this.user.profile = plainToInstance(ApiForeignOrganizationProfile, profile);
							break;
						default:
							this.user.profile = plainToInstance(ApiAccountProfileBase, profile);
					}
				}
				
				await this.fetchUserName();
			} catch (error) {
				useAppStore().handleActionError(error);
			} finally {
				this.user.isUserinfoLoading = false;
			}
		},
		async fetchUserName() {
			const externalId = this.profile?.externalId;
			
			if(!externalId) return;
			
			const controllers = {
				[TypePersonalAccount.RMP_COUNTERPARTY_APP]: async () => {
					const employee = await safeFetch(() => legalEntityController.getEmployeeById(externalId));
					
					return employee?.employee;
				},
				[TypePersonalAccount.RMP_ENTERPRISE_APP]: async () => {
					const employee = await safeFetch(() => staffController.getOwnEmployee({}));
					
					return employee?.employeePersisted?.employee;
				}
			};
			
			const typePersonalAccount = getTypePersonalAccount();
			
			if(!typePersonalAccount) return;
			
			try {
				let employee: ApiCounterpartyEmployeeEntrepreneur | ApiOfficeEmployeePersisted["employee"] | ApiCounterpartyEmployeePersistedLegalEntity["employee"] | undefined | null = null;
				
				if(this.profile?.type === ProfileType.COUNTERPARTY_ENTREPRENEUR_EMPLOYEE) {
					const data = await safeFetch(() => entrepreneurController.getEmployeeById(externalId));
					
					employee = data?.employee;
				} else {
					employee = await controllers[typePersonalAccount]();
				}
				
				if(!employee) return;
				
				this.user.firstName = employee.firstName;
				this.user.lastName = employee.lastName;
				this.user.middleName = employee.middleName ?? "";
			} catch (error) {
				useAppStore().handleActionError(error);
			}
		}
	}
});

